import React from 'react';
import { ReactSVG } from 'react-svg';
import truckIcon from '@/assets/img/truckWContainer.svg';
import styles from './LiveUnloadPlannerCard.module.css';

const STATUS_CLASS = {
  completed: styles.moveNodeCompleted,
  in_transit: styles.moveNodeInTransit,
  upcoming: styles.moveNodeUpcoming,
  current_stop: styles.moveNodeInTransit,
};

function hasMetaTime(value) {
  if (value == null) return false;
  const s = String(value).trim();
  return s !== '' && s !== '—';
}

export function PlannerCardMovementPath({ movementSegments }) {
  const steps = Array.isArray(movementSegments)
    ? movementSegments.filter(
        (seg) =>
          seg?.type === 'place' &&
          seg?.label != null &&
          String(seg.label).trim() !== ''
      )
    : [];
  if (steps.length === 0) return null;
  /** Legacy: step marked in_transit (between it and next). */
  const transitIdx = steps.findIndex((step) => step.status === 'in_transit');
  /** on_way true: truck between last history and first future (no in_transit node on last history). */
  const truckLegAfterIdx = steps.findIndex((step) => step.truckBetweenAfterThisStep);
  const betweenIdx =
    truckLegAfterIdx >= 0
      ? truckLegAfterIdx
      : transitIdx >= 0
        ? transitIdx
        : -1;
  const showTruckBetween =
    betweenIdx >= 0 && betweenIdx < steps.length - 1;

  const stoppedIdx = steps.findIndex((step) => step.status === 'current_stop');
  const truckOnStepIndex = stoppedIdx >= 0 ? stoppedIdx : -1;
  const showTruckOnStep = truckOnStepIndex >= 0 && !showTruckBetween;
  const hasInTransit =
    transitIdx >= 0 ||
    (truckLegAfterIdx >= 0 && truckLegAfterIdx < steps.length - 1);

  return (
    <aside className={styles.pathSidebar} aria-label="Movement distance path">
      <div className={styles.pathSidebarInner}>
        <div className={styles.pathTitleStack}>
          <span className={styles.pathTitleLine}>Container Movement</span>
        </div>
        <div
          className={styles.moveTimelineWrap}
          style={{ '--move-step-span-count': Math.max(steps.length - 1, 0) }}>
          <div className={styles.moveTrack} aria-hidden />
          {showTruckBetween ? (
            <ReactSVG
              src={truckIcon}
              className={styles.moveTruckIcon}
              style={{ '--truck-between-index': betweenIdx }}
              beforeInjection={(svg) => {
                svg.setAttribute('aria-hidden', 'true');
                svg.querySelectorAll('path,circle,line,polyline,polygon').forEach((el) => {
                  el.setAttribute('stroke', '#2f6bff');
                  el.setAttribute('fill', '#2f6bff');
                });
              }}
            />
          ) : null}
          {showTruckOnStep ? (
            <ReactSVG
              src={truckIcon}
              className={styles.moveTruckIconOnStep}
              style={{ '--truck-on-step-index': truckOnStepIndex }}
              beforeInjection={(svg) => {
                svg.setAttribute('aria-hidden', 'true');
                svg.querySelectorAll('path,circle,line,polyline,polygon').forEach((el) => {
                  el.setAttribute('stroke', '#2f6bff');
                  el.setAttribute('fill', '#2f6bff');
                });
              }}
            />
          ) : null}
          <div className={styles.moveStepsRow}>
            {steps.map((step, idx) => {
              const nodeClass =
                STATUS_CLASS[step.status] || styles.moveNodeUpcoming;
              const isHoverable = step.status === 'upcoming';
              return (
                <div
                  key={`s-${idx}`}
                  className={styles.moveStep}
                  title={isHoverable ? step.tooltip || '' : ''}>
                  <span
                    className={`${styles.moveStatusText} ${
                      step.status === 'completed'
                        ? styles.moveStatusTextCompleted
                        : ''
                    }`}>
                    {step.status === 'current_stop'
                      ? '\u00a0'
                      : step.statusLabel || 'Upcoming'}
                  </span>
                  <span
                    className={`${styles.moveNode} ${nodeClass} ${
                      isHoverable ? styles.moveNodeHoverable : ''
                    }`}>
                    {idx + 1}
                  </span>
                  <span className={styles.moveStepName}>{step.label}</span>
                  {step.status === 'upcoming' && step.futureEvent ? (
                    <span className={styles.moveStepEvent}>
                      {step.futureEvent}
                    </span>
                  ) : null}
                  {step.status === 'upcoming'
                    ? null
                    : hasMetaTime(step.arrive) || hasMetaTime(step.depart) ? (
                        <span className={styles.moveStepMeta}>
                          {hasMetaTime(step.arrive) ? (
                            <span>Arrive: {step.arrive}</span>
                          ) : null}
                          {hasMetaTime(step.depart) ? (
                            <span>Depart: {step.depart}</span>
                          ) : null}
                        </span>
                      ) : null}
                </div>
              );
            })}
          </div>
        </div>
        <div className={styles.moveLegend} aria-label="Movement legend">
          <span className={styles.moveLegendItem}>
            <span
              className={`${styles.moveLegendDot} ${styles.moveLegendDotCompleted}`}
            />
            Completed
          </span>
          {/* {hasInTransit ? (
            <span className={styles.moveLegendItem}>
              <span
                className={`${styles.moveLegendDot} ${styles.moveLegendDotInTransit}`}
              />
              In Transit
            </span>
          ) : null} */}
          <span className={styles.moveLegendItem}>
            <span
              className={`${styles.moveLegendDot} ${styles.moveLegendDotUpcoming}`}
            />
            Upcoming
          </span>
          {showTruckBetween || showTruckOnStep ? (
            <span className={styles.moveLegendItem}>
              <ReactSVG
                src={truckIcon}
                className={styles.moveLegendTruck}
                beforeInjection={(svg) => {
                  svg.setAttribute('aria-hidden', 'true');
                  svg.querySelectorAll('path,circle,line,polyline,polygon').forEach((el) => {
                    el.setAttribute('stroke', '#2f6bff');
                    el.setAttribute('fill', '#2f6bff');
                  });
                }}
              />
              Current Location
            </span>
          ) : null}
        </div>
      </div>
    </aside>
  );
}
